import { z } from 'zod';

export const BRAND_NAME_FORMATTING = 'BrandNameFormatting';
export const APPROVED_SUBSTITUTIONS = 'ApprovedSubstitutions';
export const RESTRICTED_CUSTOM_TEXT = 'RestrictedCustomText';
export const AVOID_CROSS_CATEGORY_COMPARISONS = 'AvoidCrossCategoryComparisons';
export const APPROVED_NUTRIENT_RELATED_PHRASES = 'ApprovedNutrientRelatedPhrases';
export const AVOID_SPECIFIC_CLAIM_LANGUAGE = 'AvoidSpecificClaimLanguage';
export const APPROVED_ON_PACKAGE_LANGUAGE = 'ApprovedOnPackageLanguage';
export const APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS = 'ApprovedQualifierPhrasesBeforeClaims';
export const APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT = 'ApprovedProductAttributeAndDisclaimerText';
export const AVOID_RESTRICTED_TEXT_FOR_CLAIMS = 'AvoidRestrictedTextForClaims';

export const VARIANT = 'variant';

export const GUIDELINE_TYPES = [
  BRAND_NAME_FORMATTING,
  APPROVED_SUBSTITUTIONS,
  RESTRICTED_CUSTOM_TEXT,
  AVOID_CROSS_CATEGORY_COMPARISONS,
  APPROVED_NUTRIENT_RELATED_PHRASES,
  AVOID_SPECIFIC_CLAIM_LANGUAGE,
  APPROVED_ON_PACKAGE_LANGUAGE,
  APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AVOID_RESTRICTED_TEXT_FOR_CLAIMS
] as const;

/**
 * @deprecated
 * This will require the form to handle typescript enum, instead of literal strings
 * Use the above GUIDELINE_TYPES instead, or find a better way for data type conversion in Form
 */
export enum GuidelineType {
  BrandNameFormatting = BRAND_NAME_FORMATTING,
  RestrictedCustomText = RESTRICTED_CUSTOM_TEXT,
  AvoidCrossCategoryComparisons = AVOID_CROSS_CATEGORY_COMPARISONS,
  ApprovedSubstitutions = APPROVED_SUBSTITUTIONS,
  ApprovedNutrientRelatedPhrases = APPROVED_NUTRIENT_RELATED_PHRASES,
  AvoidSpecificClaimLanguage = AVOID_SPECIFIC_CLAIM_LANGUAGE,
  ApprovedOnPackageLanguage = APPROVED_ON_PACKAGE_LANGUAGE,
  ApprovedQualifierPhrasesBeforeClaims = APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS,
  ApprovedProductAttributeAndDisclaimerText = APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT,
  AvoidRestrictedTextForClaims = AVOID_RESTRICTED_TEXT_FOR_CLAIMS
}

const statusTypes = ['ACTIVE', 'ARCHIVED', 'DRAFT'] as const;

const StatusSchema = z.enum(statusTypes);
export type StatusType = z.infer<typeof StatusSchema>;

const CharacterLimitsSchema = z.object({
  titles: z.number(),
  bullets: z.number(),
  descriptions: z.number(),
  itemHighlights: z.number().optional()
});

const KeywordsSchema = z.object({
  inclusion: z.array(z.string()).default([]),
  exclusion: z.array(z.string()).default([]),
  shouldIncludeStacklineRecommendedKeywords: z.boolean().optional()
});

export const voiceTypes = ['Informative', 'Persuasive', 'Sustainable', 'Healthy', 'Playful', 'Supportive'] as const;

// @deprecated - Intensity levels removed from new form design but kept for backward compatibility
export const intensityLevels = ['High', 'Very High', 'Moderate'] as const;

export const toneTypes = ['Friendly', 'Luxurious', 'Humorous', 'Encouraging', 'Enthusiastic', 'Confident'] as const;

const VoiceSchema = z.object({
  type: z.enum(voiceTypes),
  intensity: z.enum(intensityLevels)
});

const ToneSchema = z.object({
  type: z.enum(toneTypes),
  intensity: z.enum(intensityLevels)
});

// Common base fields
const CommonGuidelineFields = {
  name: z.string(),
  type: z.enum(GUIDELINE_TYPES)
};

// Parameters for BrandNameFormatting
const BrandNameFormattingParams = z.object({
  format: z.enum([
    'in title casing',
    'with all capital letters',
    'with all lowercase letters',
    'in title casing',
    'in custom text'
  ]),
  customText: z.string().optional(),
  brandName: z.string(),
  condition: z.enum(['always', 'never'])
});

// Parameters for ApprovedSubstitutions
const ApprovedSubstitutionsParams = z.object({
  restrictedTerm: z.string(),
  approvedTerm: z.string()
});

const AvoidCrossCategoryComparisonsSchema = z.object({
  comparisonType: z.enum(['category', 'sub-category']),
  target: z.string()
});

const RestrictedCustomTextSchema = z.object({
  restrictedText: z.string()
});

const ApprovedNutrientRelatedPhrasesSchema = z.object({
  nutrient: z.string(),
  nutrientStatement: z.string(),
  amount: z.string(),
});

const AvoidSpecificClaimLanguageSchema = z.object({
  claimType: z.enum(['about', 'such as']),
  topic: z.string()
});

const ApprovedOnPackageLanguageSchema = z.object({
  onPackageText: z.string()
});

const ApprovedQualifierPhrasesBeforeClaimsSchema = z.object({
  approvedPhrase: z.string(),
  claim: z.string()
});

const ApprovedProductAttributeAndDisclaimerTextSchema = z.object({
  productAttribute: z.string(),
  approvedReplacement: z.string(),
  disclaimer: z.string(),
  restrictedText: z.string()
});

const AvoidRestrictedTextForClaimsSchema = z.object({
  topic: z.string(),
  referenceText: z.string()
});

// Discriminated union based on "type"
export const GuidelineSchema = z.discriminatedUnion('type', [
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(BRAND_NAME_FORMATTING),
    parameters: BrandNameFormattingParams
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(APPROVED_SUBSTITUTIONS),
    parameters: ApprovedSubstitutionsParams
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(RESTRICTED_CUSTOM_TEXT),
    parameters: RestrictedCustomTextSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(AVOID_CROSS_CATEGORY_COMPARISONS),
    parameters: AvoidCrossCategoryComparisonsSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(APPROVED_NUTRIENT_RELATED_PHRASES),
    parameters: ApprovedNutrientRelatedPhrasesSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(AVOID_SPECIFIC_CLAIM_LANGUAGE),
    parameters: AvoidSpecificClaimLanguageSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(APPROVED_ON_PACKAGE_LANGUAGE),
    parameters: ApprovedOnPackageLanguageSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(APPROVED_QUALIFIER_PHRASES_BEFORE_CLAIMS),
    parameters: ApprovedQualifierPhrasesBeforeClaimsSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(APPROVED_PRODUCT_ATTRIBUTE_AND_DISCLAIMER_TEXT),
    parameters: ApprovedProductAttributeAndDisclaimerTextSchema
  }),
  z.object({
    ...CommonGuidelineFields,
    type: z.literal(AVOID_RESTRICTED_TEXT_FOR_CLAIMS),
    parameters: AvoidRestrictedTextForClaimsSchema
  })
]);

export const SettingsSchema = z.object({
  voice: VoiceSchema,
  tone: ToneSchema,
  bulletPointsCount: z.number(),
  keywords: KeywordsSchema,
  characterLimits: CharacterLimitsSchema,
  aiInstructions: z.string().optional(),
  guidelines: z.array(GuidelineSchema).nullish(),
  keywordDensity: z.number().min(0).max(100)
});

export const BaseSettingsFormSchema = z.object({
  settings: SettingsSchema
});

export const BaseFormProfileSchema = z.object({
  profileName: z
    .string()
    .nonempty('Profile name cannot be empty')
    .min(3, 'Profile name must have at least 3 characters')
    .max(100, 'Profile name cannot be more than 100 letters'),
  profileDescription: z
    .string()
    .nonempty('Description cannot be empty')
    .max(100, 'Description must be less than 100 letters'),
  settings: SettingsSchema
});

/**
 * Only available for an existing profile
 */
const EditExtraFieldsSchema = z.object({
  profileId: z.string().min(1, 'Profile ID is not provided for editting'),
  version: z.number().optional(),
  isCustomProfile: z.boolean(),
  createdAt: z.string().optional(),
  createdBy: z.string().optional(),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
  status: StatusSchema,
  profileType: z.string()
});

export const FinalFormProfileSchema = z.intersection(
  BaseFormProfileSchema,
  z.discriminatedUnion(VARIANT, [
    z.object({ variant: z.literal('add') }),
    z
      .object({
        variant: z.literal('edit')
      })
      .merge(EditExtraFieldsSchema)
  ])
);

const ExistingProfileSchema = BaseFormProfileSchema.merge(EditExtraFieldsSchema);

export const GetProfileSchema = ExistingProfileSchema.merge(
  z.object({
    beaconClientId: z.number()
  })
);

const GetSingleProfileResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: GetProfileSchema
});

const GetProfilesResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: z.array(GetProfileSchema)
});

const CreateProfileResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: ExistingProfileSchema
});

const UpdateProfileResponseSchema = z.object({
  isSuccess: z.boolean(),
  data: ExistingProfileSchema
});

export type GetProfile = z.infer<typeof GetProfileSchema>;
export type ExistingProfile = z.infer<typeof ExistingProfileSchema>;
export type BaseFormProfile = z.infer<typeof BaseFormProfileSchema>;
export type Settings = z.infer<typeof SettingsSchema>;
export type BaseSettingsForm = z.infer<typeof BaseSettingsFormSchema>;
export type Voice = z.infer<typeof VoiceSchema>;
export type Tone = z.infer<typeof ToneSchema>;
export type Keywords = z.infer<typeof KeywordsSchema>;
export type CharacterLimits = z.infer<typeof CharacterLimitsSchema>;
export type Guideline = z.infer<typeof GuidelineSchema>;
export type FinalFormProfile = z.infer<typeof FinalFormProfileSchema>;

export type GetSingleProfileResponse = z.infer<typeof GetSingleProfileResponseSchema>;
export type GetProfilesResponse = z.infer<typeof GetProfilesResponseSchema>;
export type CreateProfileResponse = z.infer<typeof CreateProfileResponseSchema>;
export type UpdateProfileResponse = z.infer<typeof UpdateProfileResponseSchema>;

export type GetProfilesQueryParams = {
  status?: StatusType;
  profileName?: string;
  retailerId?: number;
};

export type GetSingleProfileQueryParams = {
  profileId: string;
  version?: string;
};
